import { CheckCircle, Star } from "@tamagui/lucide-icons";
import { observer } from "mobx-react-lite";
import { useCallback, useMemo, useState } from "react";
import { YStack } from "tamagui";
import { Body } from "../../../ui/Body";
import { Button } from "../../../ui/Button";
import { Sheet } from "../../../ui/Sheet";
import { TipsText } from "../../../ui/TipsText";
import { Title } from "../../../ui/Title";
import { DataItemsSelect } from "../../elements/DataItemsSelect";
import { RatingUsers } from "../../elements/rating/RatingUI";
import type LocationModuleModel from "../LocationModuleModel";

export const LocationDoneSheet: React.FC<{ module: LocationModuleModel }> =
  observer(({ module }) => {
    const [selectedLocations, setSelectedLocations] = useState<string[]>([]);

    const handleLocationSelect = useCallback(
      (newLocation: string) => {
        if (selectedLocations.includes(newLocation)) {
          setSelectedLocations((locations) =>
            locations.filter((location) => location !== newLocation),
          );
        } else {
          setSelectedLocations((locations) => locations.concat([newLocation]));
        }
      },
      [selectedLocations],
    );

    const items = useMemo(() => {
      return module.locationItems
        .map((location) => ({
          uid: location.uid,
          text: location.data.label,
          value: location.data.uid,
          ratings: module.ratings,
        }))
        .sort(
          (a, b) => b.ratings.getAverage(b.uid) - a.ratings.getAverage(a.uid),
        );
    }, [module.ratings, module.locationItems]);

    return (
      <Sheet
        sheetId="location_done"
        open={module.doneFlowEnabled}
        onOpenChange={module.disableDoneFlow.bind(module)}
        header={
          <YStack gap="$space.small">
            <Title>Decide final location(s)</Title>
            <Body minHeight={40}>
              {selectedLocations.length
                ? `${selectedLocations.length} location(s) selected`
                : "Select the location(s) you've decided on to mark the card as completed"}
            </Body>
          </YStack>
        }
        footer={
          <YStack gap="$medium">
            <TipsText text="When saved you will see the final locations in your Planira" />
            <Button
              onPress={() => module.setDone(selectedLocations)}
              disabled={!selectedLocations.length}
              icon={CheckCircle}
            >
              Save locations
            </Button>
          </YStack>
        }
      >
        <DataItemsSelect
          module={module}
          onItemPress={handleLocationSelect}
          activeItems={selectedLocations}
          getInfoRowItems={useCallback(
            ({ dataUid }: { dataUid?: string }) => {
              if (!dataUid) {
                return [];
              }

              return [
                {
                  icon: Star,
                  text: (
                    <RatingUsers
                      ratingsManager={module.ratings}
                      dataUid={dataUid}
                    />
                  ),
                },
              ];
            },
            [module.ratings],
          )}
          items={items}
        />
      </Sheet>
    );
  });
